import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import type { GymPhoto } from '@atomfit/shared';
import { gymPhotosApi, photoUrl } from '../../api/gym-photos.api.js';
import { translate as t } from '../../i18n/index.js';

function PhotoImage({
  photo,
  gymName,
  full = false,
}: {
  photo: GymPhoto;
  gymName: string;
  full?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  return failed ? (
    <button
      type="button"
      className="af-photo-retry"
      onClick={(event) => {
        event.stopPropagation();
        setFailed(false);
        setAttempt((n) => n + 1);
      }}
    >
      {t('gymPhotos.retry')}
    </button>
  ) : (
    <img
      key={attempt}
      src={photoUrl(full ? photo.fullUrl : photo.thumbnailUrl)}
      width={full ? photo.width : photo.thumbnailWidth}
      height={full ? photo.height : photo.thumbnailHeight}
      alt={t('gymPhotos.alt', { gym: gymName, number: photo.position + 1 })}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

export function GymPhotoGallery({ gymId, gymName }: { gymId: string; gymName: string }) {
  const [photos, setPhotos] = useState<GymPhoto[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [active, setActive] = useState<number | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    setStatus('loading');
    setPhotos([]);
    setActive(null);
    gymPhotosApi
      .list(gymId, abort.signal)
      .then(({ data }) => {
        setPhotos(data);
        setStatus('ready');
      })
      .catch(() => {
        if (!abort.signal.aborted) setStatus('error');
      });
    return () => abort.abort();
  }, [gymId, attempt]);
  if (status === 'ready' && !photos.length) return null;
  return (
    <section className="af-gym-photos" aria-label={t('gymPhotos.title')}>
      <div className="af-gym-photos-heading">
        <h3>
          {t('gymPhotos.title')}
          <span className="af-landing-title-dot">.</span>
        </h3>
        {photos.length > 0 && (
          <button type="button" onClick={() => setActive(0)}>
            {t('gymPhotos.viewAll', { count: photos.length })}{' '}
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        )}
      </div>
      {status === 'loading' && <p role="status">{t('gymPhotos.loading')}</p>}
      {status === 'error' && (
        <p role="alert">
          {t('gymPhotos.loadError')}{' '}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            {t('gymPhotos.retry')}
          </button>
        </p>
      )}
      <div className={`af-gym-photo-preview af-gym-photo-preview--${Math.min(photos.length, 3)}`}>
        {photos.slice(0, 3).map((photo, index) => (
          <div className="af-gym-photo-preview-item" key={photo.id}>
            <PhotoImage photo={photo} gymName={gymName} />
            <button
              className="af-gym-photo-open"
              type="button"
              aria-label={t('gymPhotos.open', { number: index + 1 })}
              onClick={() => setActive(index)}
              aria-haspopup="dialog"
            />
          </div>
        ))}
      </div>
      {active !== null && (
        <GymPhotoViewer
          photos={photos}
          gymName={gymName}
          initialIndex={active}
          onClose={() => setActive(null)}
        />
      )}
    </section>
  );
}

function GymPhotoViewer({
  photos,
  gymName,
  initialIndex,
  onClose,
}: {
  photos: GymPhoto[];
  gymName: string;
  initialIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const photo = photos[index];
  const move = (direction: number) =>
    setIndex((n) => (n + direction + photos.length) % photos.length);
  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    dialog?.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      dialog?.close();
      document.body.style.overflow = overflow;
      previousFocus?.focus({ preventScroll: true });
    };
  }, []);
  if (!photo) return null;
  return createPortal(
    <dialog
      className="af-gym-photo-viewer"
      data-gym-photo-viewer
      ref={dialogRef}
      aria-label={`${gymName} — ${t('gymPhotos.title')}`}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          move(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }}
      onTouchStart={(event) => {
        const point = event.touches[0];
        touch.current =
          event.touches.length === 1 && point ? { x: point.clientX, y: point.clientY } : null;
      }}
      onTouchEnd={(event) => {
        const point = event.changedTouches[0];
        const start = touch.current;
        touch.current = null;
        if (
          point &&
          start &&
          Math.abs(point.clientX - start.x) > 60 &&
          Math.abs(point.clientY - start.y) < 60
        )
          move(point.clientX < start.x ? 1 : -1);
      }}
    >
      <button
        type="button"
        className="af-gym-photo-viewer-close"
        autoFocus
        aria-label={t('gymPhotos.close')}
        onClick={onClose}
      >
        <X />
      </button>
      <PhotoImage key={photo.id} photo={photo} gymName={gymName} full />
      <div className="af-gym-photo-viewer-controls">
        <button
          type="button"
          disabled={photos.length < 2}
          aria-label={t('gymPhotos.previous')}
          onClick={() => move(-1)}
        >
          <ArrowLeft />
        </button>
        <span aria-live="polite" aria-atomic="true">
          {index + 1} / {photos.length}
        </span>
        <button
          type="button"
          disabled={photos.length < 2}
          aria-label={t('gymPhotos.next')}
          onClick={() => move(1)}
        >
          <ArrowRight />
        </button>
      </div>
    </dialog>,
    document.body,
  );
}
